import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import fuzzaldrin from 'fuzzaldrin-plus'
import VirtualList from 'react-tiny-virtual-list'
import { Pane, Table } from 'evergreen-ui'
import { isEqual } from 'lodash'
import OptionGroupShapePropType from './OptionGroupShapePropType'
import Option from './Option'
import Group from './Group'

/**
 * This is the default item renderer of options
 * you can pass custom renderers as long as they work the same as the Option
 */
const itemRenderer = props => <Option {...props} />

export default class OptionsList extends PureComponent {
  static propTypes = {
    groups: PropTypes.arrayOf(OptionGroupShapePropType),
    close: PropTypes.func,
    height: PropTypes.number,
    width: PropTypes.number,

    /**
     * This holds the values of the options
     */
    selected: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    ),
    onSelect: PropTypes.func,
    onDeselect: PropTypes.func,
    onFilterChange: PropTypes.func,
    hasFilter: PropTypes.bool,
    optionSize: PropTypes.number,
    renderItem: PropTypes.func,
    placeholder: PropTypes.string,
    defaultSearchValue: PropTypes.string,
    emptyView: PropTypes.func
  }

  static defaultProps = {
    groups: [],
    /**
     * Including border bottom
     * For some reason passing height to TableRow doesn't work
     * TODO: fix hacky solution
     */
    optionSize: 33,
    onSelect: () => {},
    onDeselect: () => {},
    onFilterChange: () => {},
    selected: [],
    renderItem: itemRenderer,
    placeholder: 'Filter...',
    defaultSearchValue: ''
  }

  constructor(props, context) {
    super(props, context)

    const openGroups = {}

    for (const [groupIndex, group] of props.groups.entries()) {
      if (group.defaultIsOpen) {
        openGroups[group.label] = true
      }

      if (groupIndex === props.selectedGroupIndex) {
        openGroups[group.label] = true
      }
    }

    this.state = {
      searchValue: props.defaultSearchValue,
      selected: null,
      openGroups
    }
  }

  static getDerivedStateFromProps({ selected }) {
    return { selected }
  }

  componentDidMount() {
    const { hasFilter } = this.props
    if (!hasFilter) return
    /**
     * Hacky solution for broken autoFocus
     * https://github.com/segmentio/evergreen/issues/90
     */
    window.setTimeout(() => {
      this.searchRef.querySelector('input').focus()
    }, 1)
  }

  isSelected = item => {
    const { selected } = this.state

    return Boolean(
      selected.find(selectedItem => {
        if (typeof selectedItem === 'string') {
          return selectedItem === item.value
        }

        return isEqual(selectedItem, item)
      })
    )
  }

  handleChange = searchValue => {
    this.setState({
      searchValue
    })
    this.props.onFilterChange(searchValue)
  }

  handleSelect = item => {
    this.props.onSelect(item)
  }

  handleDeselect = item => {
    this.props.onDeselect(item)
  }

  handleToggleGroup = label => {
    const openGroups = { ...this.state.openGroups }
    openGroups[label] = !openGroups[label]

    this.setState({ openGroups })
  }

  assignSearchRef = ref => {
    this.searchRef = ref
  }

  render() {
    const { searchValue } = this.state
    const {
      groups,
      close,
      width,
      height,
      onSelect,
      onDeselect,
      onFilterChange,
      selected,
      hasFilter,
      optionSize,
      renderItem,
      placeholder,
      defaultSearchValue,
      selectedGroupIndex,
      emptyView,
      ...props
    } = this.props

    const options = []

    for (const [groupIndex, group] of groups.entries()) {
      options.push({
        type: 'group',
        label: group.label,
        icon: group.icon,
        iconColor: group.iconColor,
        tooltip: group.tooltip,
        groupIndex
      })

      const isOpen = this.state.openGroups[group.label]
      if (!isOpen) {
        continue
      }

      if (searchValue.trim().length > 0) {
        const filteredOptions = fuzzaldrin.filter(group.options, searchValue, {
          key: 'label'
        })
        options.push(...filteredOptions)
      } else {
        options.push(...group.options)
      }
    }

    const isEmpty = options.length === 0
    const listHeight = height - (hasFilter ? 32 : 0)

    return (
      <Pane
        height={height}
        width={width}
        display="flex"
        flexDirection="column"
        {...props}
      >
        {hasFilter && (
          <Table.Head>
            <Table.SearchHeaderCell
              onChange={this.handleChange}
              innerRef={this.assignSearchRef}
              borderRight={null}
              height={32}
            />
          </Table.Head>
        )}

        <Pane flex={1}>
          {isEmpty && emptyView && emptyView({ listHeight })}
          <VirtualList
            height={listHeight}
            width="100%"
            itemSize={optionSize}
            itemCount={options.length}
            overscanCount={3}
            scrollToAlignment="auto"
            renderItem={({ index, style }) => {
              const item = options[index]

              if (item.type === 'group') {
                const isOpen = Boolean(this.state.openGroups[item.label])

                return (
                  <Group
                    key={item.label}
                    label={item.label}
                    style={style}
                    height={optionSize}
                    icon={item.icon}
                    iconColor={item.iconColor}
                    tooltip={item.tooltip}
                    isOpen={isOpen || selectedGroupIndex === item.groupIndex}
                    onToggle={() => this.handleToggleGroup(item.label)}
                  />
                )
              }

              return renderItem({
                key: item.value,
                label: item.label,
                style,
                height: optionSize,
                onSelect: () => this.handleSelect(item),
                onDeselect: () => this.handleDeselect(item),
                isSelected: this.isSelected(item)
              })
            }}
          />
        </Pane>
      </Pane>
    )
  }
}
